import type { SimEvent } from "../protocol/events";
import type { Composition } from "../protocol/snapshot";
import { species, totalUnits } from "./composition";
import { newPacket, resolvePropertyRecipe, type EngineNode, type EngineState } from "./state";

/**
 * Phase conversion for one processing node (spec 0003, `01-simulation-model.md`
 * `00-pillars.md` §4's "Phase Converters"). Runs inside step 2's node loop,
 * immediately after
 * `processReactions` — no tick step is added.
 *
 * A conversion is a per-packet state change, not a multiple of a ratio: at
 * most one packet per node per tick is split, exempt from the limiting-reagent
 * scaling `reactions.ts` gives recipes (spec 0008 Open Question 5).
 */
export function processPhaseConversion(state: EngineState, node: EngineNode, events: SimEvent[]): void {
  if (node.isDestroyed || node.kind !== "phaseConverter") return;
  // `03-hazards.md` §2.5/§3.3: a sludged or infested node converts at 0.5
  // units/tick too, gated by the same decisions `pushFromNode` reads
  // (`resolveSludgeGate`/`resolveInfestationGate`, tick.ts).
  if (!node.sludgeActiveThisTick || !node.verminActiveThisTick) return;

  const recipe = resolvePropertyRecipe(state, node);
  const threshold = recipe?.boilingPoint ?? null;
  if (threshold === null) return;

  // Only one extra packet fits: a conversion turns 1 packet into up to 2,
  // net +1 in the buffer before either leaves in the movement step.
  if (node.buffer.length + 1 > node.bufferCapacity) return;

  // Buffer order is already insertion order, which is deterministic — first
  // qualifying packet wins, same as every other "pick one" rule in the engine.
  const source = node.buffer.find((p) => p.temperature >= threshold);
  if (!source) return;

  const volatileSpecies = recipe?.volatileSpecies ?? [];
  const vapourComposition: Composition = {};
  const liquidComposition: Composition = {};
  for (const key of species(source.composition)) {
    const amount = source.composition[key] ?? 0;
    if (volatileSpecies.includes(key)) vapourComposition[key] = amount;
    else liquidComposition[key] = amount;
  }

  // Id allocation order is vapour-then-liquid, and only for fractions that
  // actually end up non-empty — a null fraction never claims a packet id.
  const vapourPacket =
    totalUnits(vapourComposition) > 0
      ? newPacket(state, vapourComposition, {
          isProduct: true,
          temperature: source.temperature,
          phaseRole: "vapour",
        })
      : null;
  const liquidPacket =
    totalUnits(liquidComposition) > 0
      ? newPacket(state, liquidComposition, {
          isProduct: true,
          temperature: source.temperature,
          phaseRole: "liquid",
        })
      : null;

  const sourceIndex = node.buffer.indexOf(source);
  node.buffer.splice(sourceIndex, 1);
  if (vapourPacket) node.buffer.push(vapourPacket);
  if (liquidPacket) node.buffer.push(liquidPacket);

  events.push({
    type: "packet.phaseConverted",
    nodeId: node.id,
    sourcePacketId: source.id,
    vapourPacketId: vapourPacket?.id ?? null,
    vapourComposition,
    liquidPacketId: liquidPacket?.id ?? null,
    liquidComposition,
  });
}
